import { useState } from 'react';

import { SWReactIcons } from '@similarweb/icons';

import { StyledDropdownOption } from '../../../components/Dropdown/styles';
import { Tooltip } from '../../../components/Tooltip';
import type { CompanyCrmInfoAccount } from '../../../domain/company';
import { Crm, CrmDisplayName } from '../../../domain/crm';
import { useCrmStatusQuery } from '../../../features/crm/useCrmStatusQuery';
import { translate } from '../../../services/i18n';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { StyledDropdown, StyledInfoButton, StyledInfoButtonWrapper } from '../CompanyActionsContainer/styles';

type CrmInfoButtonProps = {
  crm: Crm;
  accounts: CompanyCrmInfoAccount[];
};

export const CrmInfoButton = ({ crm, accounts }: CrmInfoButtonProps) => {
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);

  const { data: crmStatus } = useCrmStatusQuery(crm);

  const handleClickInfoButton = () => setIsDropdownOpened(true);

  const handleCloseDropdown = () => setIsDropdownOpened(false);

  const handleTrackOpenInCrm = () =>
    dispatchTrackEventWithLocation('company data', 'see company in CRM', `see in ${crm}`);

  return (
    <StyledInfoButtonWrapper>
      <Tooltip
        placement="top"
        content={translate('company.export_to_crm.open', { crm: CrmDisplayName[crm] })}
        minWidth={crm === Crm.salesforce ? 142 : 130}
      >
        {accounts.length === 1 ? (
          <StyledInfoButton data-automation="CrmInfoButtonCrm">
            <a target="_blank" rel="noreferrer" href={accounts[0].url} onMouseDown={handleTrackOpenInCrm}>
              <SWReactIcons iconName={crm} size="xs" />
            </a>
          </StyledInfoButton>
        ) : (
          <>
            <StyledInfoButton
              onClick={handleClickInfoButton}
              isActive={isDropdownOpened}
              data-automation="CrmInfoButtonDd"
            >
              <SWReactIcons iconName={crm} size="xs" />
            </StyledInfoButton>
            <StyledDropdown
              isOpened={isDropdownOpened}
              onClose={handleCloseDropdown}
              minWidth={234}
              placement="right"
              data-automation="CrmInfoDd"
            >
              {accounts.map((account) => (
                <a
                  href={account.url}
                  key={account.url}
                  target="_blank"
                  rel="noreferrer"
                  onMouseDown={handleTrackOpenInCrm}
                >
                  <StyledDropdownOption>
                    {account.companyName}
                    {crmStatus?.isCrmLabelsEnabled && account.ownerName
                      ? ` ${translate('company.export_to_crm.owned')} ${account.ownerName}`
                      : null}
                  </StyledDropdownOption>
                </a>
              ))}
            </StyledDropdown>
          </>
        )}
      </Tooltip>
    </StyledInfoButtonWrapper>
  );
};
